import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, Calendar, Shield, ArrowLeft, Edit2, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const Profile = () => {
  const { user, logout, updateUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [newEmail, setNewEmail] = useState(user?.email || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const memberSince = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long' });

  const handleUpdateEmail = async () => {
    const normalizedEmail = newEmail.trim().toLowerCase();

    if (!normalizedEmail || normalizedEmail === (user?.email || '').trim().toLowerCase()) {
      setIsEditing(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      setError(t('profile_invalid_email'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.put(
        '/auth/profile/update-email/',
        { email: normalizedEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        updateUser({
          ...user,
          email: response.data.email,
        });
        setNewEmail(response.data.email);
        setSuccess(t('profile_email_updated'));
        setTimeout(() => {
          setIsEditing(false);
          setSuccess('');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('profile_email_update_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t('back')}
        </button>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
            <h1 className="text-2xl font-bold text-white">{t('profile_title')}</h1>
            <p className="text-blue-100 text-sm mt-1">{t('profile_subtitle')}</p>
          </div>

          <div className="p-6 space-y-6">
            <div className="flex justify-center">
              <div className="h-24 w-24 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="h-12 w-12 text-blue-600" />
              </div>
            </div>

            <div className="border-t pt-6 space-y-4">
              <div className="flex items-start space-x-3">
                <User className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-500">{t('profile_username')}</label>
                  <p className="mt-1 text-lg font-medium text-gray-900">{user?.username || t('profile_not_logged_in')}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-500">{t('profile_email')}</label>
                  {isEditing ? (
                    <div className="mt-1 space-y-2">
                      <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={t('profile_enter_new_email')}
                        disabled={loading}
                      />
                      <div className="flex space-x-2">
                        <button
                          onClick={handleUpdateEmail}
                          disabled={loading}
                          className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-1"
                        >
                          <Check className="h-4 w-4" />
                          <span>{loading ? t('profile_saving') : t('save')}</span>
                        </button>
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setNewEmail(user?.email || '');
                            setError('');
                          }}
                          className="px-3 py-1 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors flex items-center space-x-1"
                        >
                          <X className="h-4 w-4" />
                          <span>{t('cancel')}</span>
                        </button>
                      </div>
                      {error && <p className="text-red-600 text-sm">{error}</p>}
                      {success && <p className="text-green-600 text-sm">{success}</p>}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <p className="mt-1 text-lg text-gray-900">{user?.email || t('profile_no_email')}</p>
                      <button onClick={() => setIsEditing(true)} className="text-blue-600 hover:text-blue-700 transition-colors">
                        <Edit2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-500">{t('profile_member_since')}</label>
                  <p className="mt-1 text-lg text-gray-900">{memberSince}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Shield className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-500">{t('profile_account_status')}</label>
                  <p className="mt-1">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{t('profile_active')}</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t pt-6 flex space-x-3">
              <button onClick={() => navigate('/dashboard')} className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
                {t('profile_dashboard')}
              </button>
              <button
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('profile_sign_out')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
